import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/mobile.css';
import App from './app/App';
import reportWebVitals from './reportWebVitals';

// ============================================================
// Global Chunk Loading Error Handler
// Catches chunk loading errors that occur outside React components
// and triggers automatic refresh to get the latest app version
// ============================================================
const isChunkLoadError = (error) => {
  if (!error) return false;
  const errorMessage = error.message || error.reason?.message || String(error);

  const chunkErrorPatterns = [
    /loading chunk/i,
    /Loading chunk/i,
    /ChunkLoadError/i,
    /failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /Unable to preload CSS/i,
    /Failed to load resource.*\.js/i,
    /Failed to load resource.*\.css/i,
    /Unexpected token '<'/i,
    /SyntaxError: Unexpected token/i,
  ];

  return chunkErrorPatterns.some(pattern => pattern.test(errorMessage));
};

// Track if we've already triggered a refresh to prevent infinite loops
let chunkErrorRefreshTriggered = false;

const handleChunkError = async () => {
  if (chunkErrorRefreshTriggered) return;
  chunkErrorRefreshTriggered = true;

  console.log('🔄 Chunk loading error detected globally - refreshing to get latest version...');

  try {
    // Clear caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
  } catch (e) {
    console.warn('Cache clearing failed:', e);
  }

  // Force reload
  window.location.reload(true);
};

// Global error handler for uncaught errors
window.addEventListener('error', (event) => {
  if (isChunkLoadError(event.error || event)) {
    event.preventDefault();
    handleChunkError();
  }
});

// Global handler for unhandled promise rejections (dynamic imports)
window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    handleChunkError();
  }
});

// ============================================================

const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args) => {
  if (typeof args[0] === 'string' && 
      (args[0].includes('Failed to parse source map') || 
       args[0].includes('Module Warning'))) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  if (typeof args[0] === 'string' && 
      (args[0].includes('Failed to parse source map') || 
       args[0].includes('Module Warning'))) {
    return;
  }
  originalWarn.apply(console, args);
};

// Install console noise filter in development
if (process.env.NODE_ENV !== "production") {
  import("./utils/logging/filter3PConsoleNoise").then(m => m.installConsoleNoiseFilter());
}

const rootElement = document.getElementById('root');

if (!window.__FAITHINSIGHT_PORTAL_ROOT_MOUNTED__) {
  window.__FAITHINSIGHT_PORTAL_ROOT_MOUNTED__ = true;
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Register service worker for app updates (only in production)
// Note: Service worker registration is now handled by Workbox in App.js
// if (process.env.NODE_ENV === 'production') {
//   register({
//     onUpdate: (registration) => {
//       // New service worker available
//       // The service worker will handle the update notification
//     },
//     onSuccess: (registration) => {
//       // Service worker registered successfully
//     }
//   });
// }
